/**
 * Switching the emailed second factor on and off.
 *
 * Enabling is two steps on purpose, and the server enforces it. A code goes to
 * the address first and has to come back before the setting is recorded, so an
 * address that cannot receive mail can never become a login requirement -
 * which, once set, would lock the account permanently: correcting the address
 * needs a sign-in, and the sign-in would need the code that never arrives.
 */

import api from "./api";

export type ActiveFactor = "passkey" | "email" | "none";

export interface TwoFactorState {
  /** Derived by the backend from the same rule the login flow uses. */
  activeFactor: ActiveFactor;
  emailOtpEnabled: boolean;
  hasPasskey: boolean;
  emailAddress: string | null;
}

export interface ActivationStart {
  /** Names the pending session; handed back on confirm. */
  mfaToken: string;
  /** False when a code was already in flight and is still good. */
  sent: boolean;
  emailAddress: string | null;
}

class TwoFactorService {
  async state(): Promise<TwoFactorState> {
    const response = await api.get("/auth/2fa");
    return response.data.data as TwoFactorState;
  }

  /** Step one: send the code. Nothing is switched on yet. */
  async startEmail(): Promise<ActivationStart> {
    const response = await api.post("/auth/2fa/email/start");
    return response.data.data as ActivationStart;
  }

  /** Step two: the code came back, so the address works. */
  async confirmEmail(mfaToken: string, code: string): Promise<void> {
    await api.post("/auth/2fa/email/confirm", { mfaToken, code });
  }

  async disableEmail(): Promise<void> {
    await api.post("/auth/2fa/email/disable");
  }
}

export const twoFactorService = new TwoFactorService();
export default twoFactorService;
